import { useQuery } from '@tanstack/react-query';
import { Quote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sellerContactNoteKey, fetchSellerContactNote } from '@/lib/dialogPrefetch';
import { linkifyHandles } from '@/lib/linkifyHandles';

interface Props {
	sellerUsername: string | null | undefined;
	className?: string;
	/** Optional heading override; defaults to "A note from @username". */
	heading?: string;
}

/**
 * Small gold-tinted callout that surfaces the seller's Contact Note
 * (profiles.contact_instructions) above any buyer-facing contact form:
 * ContactSellerDialog compose stage, MakeOfferSheet, CartContactForm.
 *
 * Renders nothing when the seller has no note (or username is missing) so
 * surfaces stay clean for sellers who haven't written one.
 *
 * Uses the shared react-query key (`sellerContactNoteKey`) so the value is
 * typically pre-warmed by `prefetchSellerContactNote` / `seedSellerContactNote`
 * and reads from cache with zero round-trip.
 */
export const SellerContactNoteCallout = ({ sellerUsername, className, heading }: Props) => {
	const enabled = Boolean(sellerUsername);
	const { data: note } = useQuery({
		queryKey: sellerContactNoteKey(sellerUsername || ''),
		queryFn: () => fetchSellerContactNote(sellerUsername as string),
		enabled,
		staleTime: 5 * 60_000,
	});

	if (!note) return null;

	return (
		<div
			className={cn(
				'rounded-xl border border-active-gold/30 bg-active-gold/[0.06] p-3.5',
				className,
			)}
		>
			<div className="flex items-start gap-2.5">
				<Quote className="h-3.5 w-3.5 text-active-gold/80 mt-0.5 shrink-0" aria-hidden />
				<div className="space-y-1 min-w-0">
					<p className="text-[11px] uppercase tracking-wide text-active-gold/80 font-medium">
						{heading ?? `A note from @${sellerUsername}`}
					</p>
					<p className="text-[13px] text-foreground/90 leading-relaxed whitespace-pre-wrap break-words">
						{linkifyHandles(note)}
					</p>
				</div>
			</div>
		</div>
	);
};

export default SellerContactNoteCallout;
